/**
 * Phase 4+ — Full Case Audit PDF Generator
 * 
 * Generates a comprehensive, printable PDF for compliance officers and auditors:
 * - Case metadata and authorization scope
 * - Complete audit timeline (who/what/when)
 * - Surgeon attestation and confirmations
 * - Proof Map summary (requirements satisfied)
 * - CPT reconciliation table with modifier justifications
 * - Override log (clearly flagged)
 * - Compliance disclaimer
 * 
 * Clearly states:
 * "LINK verifies and reconciles documentation and authorization.
 *  LINK does not provide clinical decision-making or claim submission."
 */

import { jsPDF } from 'jspdf';
import type { AuditTimelineEvent } from './types';
import type { ComplianceOverride, CaseComplianceStatus } from './types';
import type { ExportCptLine } from '@/lib/export/exportPackageGenerator';
import type { ProofMapSummaryItem, SurgeonConfirmationItem } from '@/lib/export/epicPdfGenerator';

export interface CaseAuditPdfInput {
  caseNumber: string;
  caseId: string;
  authReference?: string;
  dateOfService?: string;
  laterality?: string;
  surgeonName?: string;
  assistantSurgeonName?: string;
  facilityName?: string;

  /** Attestation */
  attestationText?: string;
  attestationTimestamp?: string;

  /** Operative note text */
  operativeNoteText?: string;

  /** CPT lines */
  cptLines: ExportCptLine[];
  authorizedCpts: string[];
  authorizationScope?: string;

  /** Proof map summary */
  proofMapSummary?: ProofMapSummaryItem[];

  /** Surgeon confirmations */
  surgeonConfirmations?: SurgeonConfirmationItem[];

  /** Complete audit timeline */
  auditTimeline: AuditTimelineEvent[];

  /** Override log */
  overrides: ComplianceOverride[];

  /** Compliance status */
  complianceStatus: CaseComplianceStatus;

  /** Demo mode */
  isDemoExport: boolean;
  generatedAt: string;
  generatedBy?: string;
}

export function generateCaseAuditPdf(input: CaseAuditPdfInput): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPage = (needed: number = 14) => {
    if (y > doc.internal.pageSize.getHeight() - needed - 20) {
      doc.addPage();
      y = margin;
    }
  };

  const addText = (text: string, fontSize = 9, bold = false) => {
    checkPage(fontSize * 0.6);
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * (fontSize * 0.45) + 2;
  };

  const addSection = (title: string, color: [number, number, number] = [15, 23, 42]) => {
    checkPage(16);
    y += 4;
    doc.setFillColor(...color);
    doc.rect(margin, y - 5, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin + 3, y);
    doc.setTextColor(0, 0, 0);
    y += 8;
  };

  const addKeyValue = (key: string, value: string) => {
    checkPage();
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${key}:`, margin, y);
    doc.setFont('helvetica', 'normal');
    const valLines = doc.splitTextToSize(value, contentWidth - 60);
    doc.text(valLines, margin + 60, y);
    y += valLines.length * 4 + 2;
  };

  // ─── HEADER ──────────────────────────────────────────────
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('LINK — Full Case Audit Report', margin, 14);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Case: ${input.caseNumber}`, margin, 22);
  if (input.authReference) doc.text(`Auth: ${input.authReference}`, margin, 28);
  doc.text(`Generated: ${new Date(input.generatedAt).toLocaleString()}`, pageWidth - margin - 65, 22);
  if (input.generatedBy) doc.text(`By: ${input.generatedBy}`, pageWidth - margin - 65, 28);

  // Compliance status badge
  const statusColor: [number, number, number] =
    input.complianceStatus.status === 'compliant' ? [22, 163, 74] :
    input.complianceStatus.status === 'review_required' ? [245, 158, 11] :
    [239, 68, 68];
  doc.setFillColor(...statusColor);
  doc.rect(pageWidth - margin - 45, 32, 45, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(input.complianceStatus.status.toUpperCase().replace('_', ' '), pageWidth - margin - 43, 37);

  if (input.isDemoExport) {
    doc.setFillColor(245, 158, 11);
    doc.rect(margin, 34, 65, 5, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(6);
    doc.text('DEMO — ILLUSTRATIVE ONLY — NO CLAIMS SUBMITTED', margin + 2, 38);
  }

  doc.setTextColor(0, 0, 0);
  y = 48;

  // ─── COMPLIANCE STATUS ──────────────────────────────────
  addSection('COMPLIANCE STATUS', statusColor);
  addKeyValue('Status', input.complianceStatus.status.replace('_', ' ').toUpperCase());
  addKeyValue('Attestation', input.complianceStatus.attestationsComplete ? 'Complete' : 'MISSING');
  addKeyValue('CPTs Confirmed', input.complianceStatus.allCptsConfirmed ? 'All confirmed' : 'Pending');
  addKeyValue('Export Gates', input.complianceStatus.exportGatesPass ? 'All passed' : 'BLOCKED');
  addKeyValue('Conflicts', input.complianceStatus.hasConflicts ? 'YES — review required' : 'None');
  addKeyValue('Overrides', `${input.complianceStatus.overrideCount} applied`);

  if (input.complianceStatus.blockingReasons.length > 0) {
    addText('Blocking Reasons:', 9, true);
    for (const reason of input.complianceStatus.blockingReasons) {
      addText(`  • ${reason}`, 8);
    }
  }

  // ─── CASE INFORMATION ──────────────────────────────────
  addSection('CASE INFORMATION');
  addKeyValue('Case Number', input.caseNumber);
  if (input.dateOfService) addKeyValue('Date of Service', input.dateOfService);
  if (input.laterality) addKeyValue('Laterality', input.laterality);
  if (input.surgeonName) addKeyValue('Surgeon', input.surgeonName);
  if (input.assistantSurgeonName) addKeyValue('Assistant Surgeon', input.assistantSurgeonName);
  if (input.facilityName) addKeyValue('Facility', input.facilityName);
  addKeyValue('Authorization Scope', input.authorizationScope || 'Standard');
  addKeyValue('Authorized CPTs', input.authorizedCpts.join(', '));

  // ─── SURGEON ATTESTATION ──────────────────────────────
  addSection('SURGEON ATTESTATION');
  if (input.attestationText) {
    addText(input.attestationText, 9);
    if (input.attestationTimestamp) {
      addText(`Attested: ${new Date(input.attestationTimestamp).toLocaleString()}`, 8);
    }
  } else {
    doc.setTextColor(220, 38, 38);
    addText('⚠ MISSING — Surgeon attestation not recorded', 9, true);
    doc.setTextColor(0, 0, 0);
  }

  // ─── SURGEON CONFIRMATIONS ──────────────────────────────
  if (input.surgeonConfirmations && input.surgeonConfirmations.length > 0) {
    addSection('SURGEON CONFIRMATIONS');
    for (const conf of input.surgeonConfirmations) {
      checkPage();
      const icon = conf.confirmation === 'confirmed_performed' ? '✓' :
                   conf.confirmation === 'not_performed' ? '✗' : '?';
      addText(`${icon} CPT ${conf.cptCode} — ${conf.confirmation.replace(/_/g, ' ')} → Billable: ${conf.billableStatus}${conf.confirmedAt ? ' @ ' + new Date(conf.confirmedAt).toLocaleString() : ''}`, 8);
    }
  }

  // ─── PROOF MAP SUMMARY ──────────────────────────────────
  if (input.proofMapSummary && input.proofMapSummary.length > 0) {
    addSection('DOCUMENTATION PROOF MAP');
    for (const item of input.proofMapSummary) {
      checkPage();
      const icon = item.documentationStatus === 'sufficient' ? '✓' :
                   item.documentationStatus === 'ambiguous' ? '?' : '✗';
      addText(`${icon} CPT ${item.cptCode}: ${item.requirementsSatisfied}/${item.requirementsTotal} requirements (${item.evidenceSnippetCount} evidence snippets) — ${item.documentationStatus.toUpperCase()}`, 8);
    }
  }

  // ─── CPT RECONCILIATION TABLE ──────────────────────────
  if (input.cptLines.length > 0) {
    addSection('FINAL CPT RECONCILIATION');
    y += 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Seq', margin, y);
    doc.text('Code', margin + 12, y);
    doc.text('Modifiers', margin + 40, y);
    doc.text('Units', margin + 78, y);
    doc.text('Description', margin + 92, y);
    y += 4;
    doc.setDrawColor(150);
    doc.line(margin, y - 1, pageWidth - margin, y - 1);
    y += 2;

    doc.setFont('helvetica', 'normal');
    for (const cpt of input.cptLines) {
      checkPage();
      doc.text(String(cpt.sequence), margin, y);
      doc.text(cpt.finalCodeString, margin + 12, y);
      doc.text(cpt.modifiers.join(', ') || '—', margin + 40, y);
      doc.text(String(cpt.units), margin + 78, y);
      doc.text((cpt.cptDescription || '').substring(0, 30), margin + 92, y);
      y += 5;
    }

    // Modifier rationale
    const hasExplanations = input.cptLines.some(l => Object.keys(l.modifierExplanations).length > 0);
    if (hasExplanations) {
      y += 2;
      addText('Modifier Rationale:', 9, true);
      for (const cpt of input.cptLines) {
        for (const [mod, reason] of Object.entries(cpt.modifierExplanations)) {
          addText(`  CPT ${cpt.cptCode}-${mod}: ${reason}`, 8);
        }
      }
    }
  }

  // ─── OVERRIDE LOG ──────────────────────────────────────
  if (input.overrides.length > 0) {
    addSection('OVERRIDE LOG', [217, 119, 6]);
    for (const override of input.overrides) {
      checkPage(20);
      addText(`${override.overrideType.replace(/_/g, ' ').toUpperCase()}`, 9, true);
      addText(`  Reason: ${override.reason}`, 8);
      addText(`  By: ${override.actorEmail} (${override.actorRole})`, 8);
      addText(`  At: ${new Date(override.timestamp).toLocaleString()}`, 8);
      if (override.cptCode) addText(`  CPT: ${override.cptCode}`, 8);
      y += 2;
    }
  }

  // ─── FULL AUDIT TIMELINE ──────────────────────────────
  addSection('COMPLETE AUDIT TIMELINE');
  if (input.auditTimeline.length === 0) {
    addText('No timeline entries recorded.', 9);
  } else {
    for (const event of input.auditTimeline.slice(0, 60)) {
      checkPage();
      const ts = new Date(event.timestamp).toLocaleString();
      const flags = [
        event.isOverride ? '[OVERRIDE]' : '',
        event.phiAccessed ? '[PHI]' : '',
      ].filter(Boolean).join(' ');
      addText(`[${ts}] ${event.description} — ${event.actor} (${event.actorRole})${flags ? ' ' + flags : ''}`, 7);
    }
    if (input.auditTimeline.length > 60) {
      addText(`... and ${input.auditTimeline.length - 60} more events`, 7);
    }
  }

  // ─── COMPLIANCE DISCLAIMER ──────────────────────────────
  addSection('COMPLIANCE DISCLAIMER');
  addText(
    'This system verifies and reconciles documentation and authorization. ' +
    'It does not provide clinical decision-making or claim submission.',
    9,
    true,
  );
  y += 2;
  addText(
    'This audit package documents the complete decision trail for this case. ' +
    'All clinical decisions were made by the treating surgeon. ' +
    'Administrative execution, verification, and traceability were provided by the system only.',
    8,
  );

  // ─── FOOTER ──────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(241, 245, 249);
    doc.rect(0, doc.internal.pageSize.getHeight() - 14, pageWidth, 14, 'F');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(
      'Documentation & Revenue Integrity Support | Audit Report',
      margin,
      doc.internal.pageSize.getHeight() - 6,
    );
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin - 22,
      doc.internal.pageSize.getHeight() - 6,
    );
    if (input.isDemoExport) {
      doc.setTextColor(217, 119, 6);
      doc.setFont('helvetica', 'bold');
      doc.text('DEMO — NOT FOR BILLING OR COMPLIANCE USE', pageWidth / 2 - 28, doc.internal.pageSize.getHeight() - 6);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }
  }

  return doc;
}
